/* eslint-disable no-unused-vars */

import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import { User } from "models/users/type_user";
import { keyLocalStorage } from "helpers/constants";
import { ITipoSystem } from "interfaces/typesystem";

// The Material Dashboard 2 PRO React TSUI Dashboard PRO Material main context
const MaterialUI = createContext<any>(null);

// Setting custom name for the context which is visible on react dev tools
MaterialUI.displayName = "MaterialUIContext";

// types
interface IMessage {
  open: boolean;
  text: string;
  severity?: "warning" | "success" | "info" | "error";
}
interface StateTypes {
  miniSidenav: boolean;
  transparentSidenav: boolean;
  whiteSidenav: boolean;
  sidenavColor:
    | "primary"
    | "secondary"
    | "info"
    | "success"
    | "warning"
    | "error"
    | "light"
    | "dark";
  transparentNavbar: boolean;
  fixedNavbar: boolean;
  openConfigurator: boolean;
  direction: "ltr" | "rtl";
  layout: "dashboard" | "page";
  darkMode: boolean;
  isAuthencated: boolean;
  user: User | null;
  dataConfig: ITipoSystem | null;
  message: IMessage;
  notificationMessage: number;
}

interface ActionTypes {
  type:
    | "MINI_SIDENAV"
    | "TRANSPARENT_SIDENAV"
    | "WHITE_SIDENAV"
    | "SIDENAV_COLOR"
    | "TRANSPARENT_NAVBAR"
    | "FIXED_NAVBAR"
    | "OPEN_CONFIGURATOR"
    | "DIRECTION"
    | "LAYOUT"
    | "DARKMODE"
    | "IS_AUTHENCATED"
    | "DATA_CONFIG"
    | "USER"
    | "MESSAGE"
    | "NOTIFICATION_MESSAGE";
  value: any;
}

// Material Dashboard 2 PRO React reducer
function reducer(state: StateTypes, action: ActionTypes) {
  switch (action.type) {
    case "MINI_SIDENAV": {
      return { ...state, miniSidenav: action.value };
    }
    case "TRANSPARENT_SIDENAV": {
      return { ...state, transparentSidenav: action.value };
    }
    case "WHITE_SIDENAV": {
      return { ...state, whiteSidenav: action.value };
    }
    case "SIDENAV_COLOR": {
      return { ...state, sidenavColor: action.value };
    }
    case "TRANSPARENT_NAVBAR": {
      return { ...state, transparentNavbar: action.value };
    }
    case "FIXED_NAVBAR": {
      return { ...state, fixedNavbar: action.value };
    }
    case "OPEN_CONFIGURATOR": {
      return { ...state, openConfigurator: action.value };
    }
    case "DIRECTION": {
      return { ...state, direction: action.value };
    }
    case "LAYOUT": {
      return { ...state, layout: action.value };
    }
    case "DARKMODE": {
      return { ...state, darkMode: action.value };
    }
    case "IS_AUTHENCATED": {
      return { ...state, isAuthencated: action.value };
    }
    case "USER": {
      return { ...state, user: action.value };
    }
    case "DATA_CONFIG": {
      return { ...state, dataConfig: action.value };
    }
    case "MESSAGE": {
      return { ...state, message: action.value };
    }
    case "NOTIFICATION_MESSAGE": {
      return { ...state, notificationMessage: action.value };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

function readUILayoutPrefs(): Partial<
  Pick<StateTypes, "darkMode" | "transparentSidenav" | "whiteSidenav">
> {
  try {
    const raw = localStorage.getItem(keyLocalStorage.UI_LAYOUT_PREFERENCES);
    if (!raw) return {};
    const o = JSON.parse(raw) as Record<string, unknown>;
    const out: Partial<
      Pick<StateTypes, "darkMode" | "transparentSidenav" | "whiteSidenav">
    > = {};
    if (typeof o.darkMode === "boolean") out.darkMode = o.darkMode;
    if (typeof o.transparentSidenav === "boolean")
      out.transparentSidenav = o.transparentSidenav;
    if (typeof o.whiteSidenav === "boolean") out.whiteSidenav = o.whiteSidenav;
    return out;
  } catch {
    return {};
  }
}

function getInitialControllerState(): StateTypes {
  let user: User | null = null;
  try {
    const raw = localStorage.getItem(keyLocalStorage.USER_STORAGE);
    if (raw) {
      const parsed = JSON.parse(raw) as User;
      user = {
        ...parsed,
        roles: parsed.roles ?? [],
        permissions: parsed.permissions ?? [],
        isSuperAdmin: parsed.isSuperAdmin ?? false,
        canAccessPanel: parsed.canAccessPanel ?? false,
      };
    }
  } catch {
    user = null;
  }

  let defaultConstants: ITipoSystem | null = null;
  try {
    const raw = localStorage.getItem(keyLocalStorage.DEFAULT_CONSTANTS);
    defaultConstants = raw ? (JSON.parse(raw) as ITipoSystem) : null;
  } catch {
    defaultConstants = null;
  }

  const prefs = readUILayoutPrefs();
  let transparentSidenav = prefs.transparentSidenav ?? false;
  let whiteSidenav = prefs.whiteSidenav ?? false;
  if (transparentSidenav && whiteSidenav) {
    transparentSidenav = false;
    whiteSidenav = true;
  }

  return {
    miniSidenav: false,
    transparentSidenav,
    whiteSidenav,
    sidenavColor: "info",
    transparentNavbar: true,
    fixedNavbar: true,
    openConfigurator: false,
    direction: "ltr",
    layout: "dashboard",
    darkMode: prefs.darkMode ?? false,
    isAuthencated: Boolean(user),
    user,
    dataConfig: defaultConstants,
    message: {
      open: false,
      severity: "info",
      text: "",
    },
    notificationMessage: 0,
  };
}

// Material Dashboard 2 PRO React context provider
function MaterialUIControllerProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [controller, dispatch] = useReducer(
    reducer,
    undefined,
    getInitialControllerState,
  );

  useEffect(() => {
    try {
      localStorage.setItem(
        keyLocalStorage.UI_LAYOUT_PREFERENCES,
        JSON.stringify({
          darkMode: controller.darkMode,
          transparentSidenav: controller.transparentSidenav,
          whiteSidenav: controller.whiteSidenav,
        }),
      );
    } catch {
      /* ignore quota / private mode */
    }
  }, [
    controller.darkMode,
    controller.transparentSidenav,
    controller.whiteSidenav,
  ]);

  const value = useMemo(() => [controller, dispatch], [controller, dispatch]);

  return <MaterialUI.Provider value={value}>{children}</MaterialUI.Provider>;
}

// Material Dashboard 2 PRO React custom hook for using context
function useMaterialUIController() {
  const context = useContext(MaterialUI);

  if (!context) {
    throw new Error(
      "useMaterialUIController should be used inside the MaterialUIControllerProvider."
    );
  }

  return context;
}

// Context module functions
const setMiniSidenav = (
  dispatch: (arg: { type: "MINI_SIDENAV"; value: boolean }) => void,
  value: boolean
) => dispatch({ type: "MINI_SIDENAV", value });
const setTransparentSidenav = (
  dispatch: (arg: { type: "TRANSPARENT_SIDENAV"; value: boolean }) => void,
  value: boolean
) => dispatch({ type: "TRANSPARENT_SIDENAV", value });
const setWhiteSidenav = (
  dispatch: (arg: { type: "WHITE_SIDENAV"; value: boolean }) => void,
  value: boolean
) => dispatch({ type: "WHITE_SIDENAV", value });
const setSidenavColor = (
  dispatch: (arg: {
    type: "SIDENAV_COLOR";
    value:
      | "primary"
      | "secondary"
      | "info"
      | "success"
      | "warning"
      | "error"
      | "light"
      | "dark";
  }) => void,
  value:
    | "primary"
    | "secondary"
    | "info"
    | "success"
    | "warning"
    | "error"
    | "light"
    | "dark"
) => dispatch({ type: "SIDENAV_COLOR", value });
const setTransparentNavbar = (
  dispatch: (arg: { type: "TRANSPARENT_NAVBAR"; value: boolean }) => void,
  value: boolean
) => dispatch({ type: "TRANSPARENT_NAVBAR", value });
const setFixedNavbar = (
  dispatch: (arg: { type: "FIXED_NAVBAR"; value: boolean }) => void,
  value: boolean
) => dispatch({ type: "FIXED_NAVBAR", value });
const setOpenConfigurator = (
  dispatch: (arg: { type: "OPEN_CONFIGURATOR"; value: boolean }) => void,
  value: boolean
) => dispatch({ type: "OPEN_CONFIGURATOR", value });
const setDirection = (
  dispatch: (arg: { type: "DIRECTION"; value: "ltr" | "rtl" }) => void,
  value: "ltr" | "rtl"
) => dispatch({ type: "DIRECTION", value });
const setLayout = (
  dispatch: (arg: { type: "LAYOUT"; value: "dashboard" | "page" }) => void,
  value: "dashboard" | "page"
) => dispatch({ type: "LAYOUT", value });
const setDarkMode = (
  dispatch: (arg: { type: "DARKMODE"; value: boolean }) => void,
  value: boolean
) => dispatch({ type: "DARKMODE", value });
const setIsAuthencated = (
  dispatch: (arg: { type: "IS_AUTHENCATED"; value: boolean }) => void,
  value: boolean
) => dispatch({ type: "IS_AUTHENCATED", value });
const setUser = (
  dispatch: (arg: { type: "USER"; value: User | null }) => void,
  value: User | null
) => dispatch({ type: "USER", value });

const setDataConfig = (
  dispatch: (arg: { type: "DATA_CONFIG"; value: ITipoSystem | null }) => void,
  value: ITipoSystem | null
) => dispatch({ type: "DATA_CONFIG", value });

const setMessage = (
  dispatch: (arg: { type: "MESSAGE"; value: IMessage }) => void,
  value: IMessage
) => dispatch({ type: "MESSAGE", value });

const setNotificationMessage = (
  dispatch: (arg: { type: "NOTIFICATION_MESSAGE"; value: number }) => void,
  value: number
) => dispatch({ type: "NOTIFICATION_MESSAGE", value });

export {
  MaterialUIControllerProvider,
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
  setDarkMode,
  setIsAuthencated,
  setUser,
  setMessage,
  setNotificationMessage,
  setDataConfig,
};
